import i18n from 'i18next';

import { env } from '@/config/env';
import { GEE_ALPHAEARTH_LATEST_KNOWN_AVAILABLE_YEAR } from '@/config/map';
import type { AlphaEarthIndex } from '@/features/map/types/map.types';
import { SAR_SCENE_GALLERY_INDICES } from '@/features/map/types/map.types';
import {
  resolveAlphaEarthBandsParam,
  resolveSpectralIndexPaletteForSarPreview,
} from '@/features/map/utils/alphaEarthApi';
import { getDefaultSpectralPaletteCsv } from '@/features/map/utils/alphaEarthIndexPalettePresets';
import {
  fetchAlphaEarthSarPreviewTileUrl,
  resolveSarPreviewBboxAndTileXY,
} from '@/features/map/utils/alphaEarthSarPreviewTile';
import type { FloodJob, SarFloodJobGeeFrame, SarFloodJobSceneSlide } from '@/mocks/floodJobs';
import type { AoiRemoteRgbProfile } from '@/types/flood.types';

/** Tham số một request tile gallery (index, bands, palette, profile RGB). */
export type GalleryFetchSpec = {
  index: AlphaEarthIndex;
  bands: string | null;
  palette: string | null;
  rgbProfile?: SarFloodJobGeeFrame['rgbProfile'];
};

function spectralPaletteForHydrate(index: Exclude<AlphaEarthIndex, 'RGB'>): string | null {
  const csv = getDefaultSpectralPaletteCsv(index);
  return csv || resolveSpectralIndexPaletteForSarPreview(index);
}

function buildGalleryFetchSpecs(): GalleryFetchSpec[] {
  const specs: GalleryFetchSpec[] = [];
  for (const index of SAR_SCENE_GALLERY_INDICES) {
    if (index === 'RGB') {
      specs.push({
        index: 'RGB',
        bands: resolveAlphaEarthBandsParam('RGB', 'B4,B3,B2'),
        palette: null,
        rgbProfile: 'sentinel2',
      });
      specs.push({
        index: 'RGB',
        bands: resolveAlphaEarthBandsParam('RGB', null),
        palette: null,
        rgbProfile: 'embedding',
      });
    } else {
      specs.push({
        index,
        bands: resolveAlphaEarthBandsParam(index, null),
        palette: spectralPaletteForHydrate(index),
      });
    }
  }
  return specs;
}

/** Mọi biến thể chỉ số gallery (panel có thể prefetch nền). */
export const ALL_GALLERY_FETCH_SPECS: readonly GalleryFetchSpec[] = buildGalleryFetchSpecs();

/** So khớp spec (dùng để ưu tiên prefetch đúng lựa chọn UI). */
export function gallerySpecsEqual(a: GalleryFetchSpec, b: GalleryFetchSpec): boolean {
  return (
    a.index === b.index &&
    (a.bands ?? '') === (b.bands ?? '') &&
    (a.palette ?? '') === (b.palette ?? '') &&
    a.rgbProfile === b.rgbProfile
  );
}

/**
 * Hydrate ban đầu: RGB embedding (băng mặc định A01,A16,A09 từ `resolveAlphaEarthBandsParam('RGB', null)`).
 * True color S2 (B4,B3,B2) lazy-fetch khi user chọn “S2” trên UI — tránh ô trắng khi BE/ngày không có mosaic S2.
 */
const INITIAL_HYDRATE_SPECS: readonly GalleryFetchSpec[] = [
  {
    index: 'RGB',
    bands: resolveAlphaEarthBandsParam('RGB', null),
    palette: null,
    rgbProfile: 'embedding',
  },
];

function frameIndexLabel(spec: GalleryFetchSpec): string {
  if (spec.index === 'RGB' && spec.rgbProfile === 'embedding') {
    return i18n.t('flood.aoiInsight.rgbProfile.embedding');
  }
  if (spec.index === 'RGB' && spec.rgbProfile === 'sentinel2') {
    return i18n.t('flood.aoiInsight.rgbProfile.sentinel2');
  }
  return i18n.t(`flood.aoiInsight.geeIndex.${spec.index}`);
}

/** Khóa ổn định để gộp / tránh trùng frame trong state panel. */
export function geeFrameKeyFromSpec(spec: GalleryFetchSpec): string {
  if (spec.index === 'RGB') return `RGB:${spec.rgbProfile ?? 'sentinel2'}`;
  return spec.index;
}

export function geeFrameKeyFromFrame(
  frame: Pick<SarFloodJobGeeFrame, 'index' | 'rgbProfile'>,
): string {
  if (frame.index === 'RGB') return `RGB:${frame.rgbProfile ?? 'sentinel2'}`;
  return frame.index;
}

/**
 * Dựng spec gọi API từ lựa chọn chỉ số + profile RGB trên UI.
 */
export function galleryFetchSpecFromUi(
  index: AlphaEarthIndex,
  rgbProfile: AoiRemoteRgbProfile,
): GalleryFetchSpec {
  if (index === 'RGB') {
    if (rgbProfile === 'embedding') {
      return {
        index: 'RGB',
        bands: resolveAlphaEarthBandsParam('RGB', null),
        palette: null,
        rgbProfile: 'embedding',
      };
    }
    return {
      index: 'RGB',
      bands: resolveAlphaEarthBandsParam('RGB', 'B4,B3,B2'),
      palette: null,
      rgbProfile: 'sentinel2',
    };
  }
  return {
    index,
    bands: resolveAlphaEarthBandsParam(index, null),
    palette: spectralPaletteForHydrate(index),
  };
}

/**
 * Clamp năm của `YYYY-MM-DD` về mosaic embedding mới nhất đã xác minh — `GOOGLE/SATELLITE_EMBEDDING/V1/ANNUAL`
 * chỉ phát hành theo năm, năm kịch bản (vd. 2026) có thể chưa có → BE 404.
 * Giữ nguyên tháng/ngày; nếu ngày gốc ≤ năm chuẩn thì giữ nguyên.
 */
function clampEmbeddingReferenceDate(referenceDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(referenceDate);
  if (!m) return referenceDate;
  const year = Number.parseInt(m[1], 10);
  if (!Number.isFinite(year) || year <= GEE_ALPHAEARTH_LATEST_KNOWN_AVAILABLE_YEAR) {
    return referenceDate;
  }
  return `${GEE_ALPHAEARTH_LATEST_KNOWN_AVAILABLE_YEAR}-${m[2]}-${m[3]}`;
}

/** Ngày BE nên nhận cho một spec — clamp cho embedding, giữ nguyên cho Sentinel-2 / chỉ số phổ. */
function resolveRequestDate(slide: SarFloodJobSceneSlide, spec: GalleryFetchSpec): string {
  if (spec.index === 'RGB' && spec.rgbProfile === 'embedding') {
    return clampEmbeddingReferenceDate(slide.referenceDate);
  }
  return slide.referenceDate;
}

/**
 * Tải một frame gallery (ảnh tile + caption) cho slide và spec đã cho.
 * RGB embedding (Axx / annual) được clamp về năm phát hành gần nhất đã biết;
 * nếu BE vẫn trống (vùng AOI không có ảnh), tự fallback RGB Sentinel-2 (B4,B3,B2).
 */
export async function fetchSarGalleryFrame(
  bbox: string,
  x: number,
  y: number,
  slide: SarFloodJobSceneSlide,
  spec: GalleryFetchSpec,
): Promise<SarFloodJobGeeFrame | null> {
  const load = async (s: GalleryFetchSpec): Promise<SarFloodJobGeeFrame | null> => {
    const imageUrl = await fetchAlphaEarthSarPreviewTileUrl({
      bbox,
      date: resolveRequestDate(slide, s),
      index: s.index,
      bands: s.bands,
      palette: s.palette,
      x,
      y,
    });
    if (!imageUrl?.trim()) return null;
    const indexLabel = frameIndexLabel(s);
    return {
      index: s.index,
      rgbProfile: s.rgbProfile,
      imageUrl,
      caption: i18n.t('ops.jobs.sarResult.captionAlphaEarthFrame', {
        period: slide.periodLabel,
        indexLabel,
      }),
    };
  };

  const primary = await load(spec);
  if (primary) return primary;
  if (spec.index === 'RGB' && spec.rgbProfile === 'embedding') {
    return load(galleryFetchSpecFromUi('RGB', 'sentinel2'));
  }
  return null;
}

async function hydrateOneSlide(
  bbox: string,
  x: number,
  y: number,
  slide: SarFloodJobSceneSlide,
): Promise<SarFloodJobSceneSlide> {
  const frameResults = await Promise.all(
    INITIAL_HYDRATE_SPECS.map((spec) => fetchSarGalleryFrame(bbox, x, y, slide, spec)),
  );

  const geeFrames = frameResults.filter((f): f is SarFloodJobGeeFrame => f != null);
  const rgbEmb = geeFrames.find((f) => f.index === 'RGB' && f.rgbProfile === 'embedding');
  const rgbS2 = geeFrames.find((f) => f.index === 'RGB' && f.rgbProfile === 'sentinel2');
  const rgbAny = geeFrames.find((f) => f.index === 'RGB');
  const legacyUrl =
    rgbEmb?.imageUrl ?? rgbS2?.imageUrl ?? rgbAny?.imageUrl ?? geeFrames[0]?.imageUrl;

  return {
    ...slide,
    geeFrames,
    imageUrl: legacyUrl,
    caption:
      legacyUrl != null
        ? i18n.t('ops.jobs.sarResult.captionAlphaEarth', { period: slide.periodLabel })
        : undefined,
  };
}

/**
 * Hydrate gallery: một khung RGB embedding mỗi slide; Sentinel-2 true color và chỉ số khác do panel lazy-fetch / prefetch nền.
 */
export async function hydrateSarSceneGalleryAlphaEarth(
  job: FloodJob,
  slides: SarFloodJobSceneSlide[],
): Promise<SarFloodJobSceneSlide[]> {
  if (!env.SAR_RESULT_ALPHAEARTH_PREVIEW) return slides;
  const geom = job.aoiPolygon;
  if (!geom) return slides;

  const tile = resolveSarPreviewBboxAndTileXY(geom);
  if (!tile) return slides;

  return Promise.all(slides.map((slide) => hydrateOneSlide(tile.bbox, tile.x, tile.y, slide)));
}
